/**
 * Product names in SFCC often include a variant suffix after " - " (e.g. "Standard Shelf - Natural Oak").
 * Flyout footer summary only shows the label before that delimiter.
 * @param {string} name
 * @returns {string}
 */
export function selectionSummaryDisplayLabel(name) {
  if (!name || typeof name !== 'string') return '';

  const trimmed = name.trim();
  const delimiterIndex = trimmed.indexOf(' - ');

  return delimiterIndex === -1 ? trimmed : trimmed.slice(0, delimiterIndex).trim();
}

/**
 * Split "Standard Shelf - Natural Oak" → ["Standard Shelf", "Natural Oak"] for strip summary.
 * @param {string} name
 * @returns {string[]}
 */
export function expandStripSelectionSummaryParts(name) {
  if (!name || typeof name !== 'string') return [];

  return name
    .trim()
    .split(' - ')
    .map((segment) => segment.trim())
    .filter(Boolean);
}

/**
 * @param {Object|null|undefined} selections
 * @param {(name: string) => string[]} expandName
 * @returns {string[]}
 */
function collectSelectionSummaryParts(selections, expandName) {
  if (!selections) return [];

  const parts = [];

  if (selections.cover?.name) {
    for (const label of expandName(selections.cover.name)) {
      if (label) parts.push(label);
    }
  }

  Object.entries(selections).forEach(([stepId, slots]) => {
    if (stepId === 'cover' || stepId === 'optionalAddOns') return;
    if (!Array.isArray(slots)) return;

    slots.forEach((slot) => {
      if (slot?.isAutoSelected) return;
      if (!slot?.name) return;
      for (const label of expandName(slot.name)) {
        if (label) parts.push(label);
      }
    });
  });

  const addons = selections.optionalAddOns?.addons;

  if (Array.isArray(addons)) {
    addons.forEach((slot) => {
      if (!slot?.name) return;
      for (const label of expandName(slot.name)) {
        if (label) parts.push(label);
      }
    });
  }

  const seen = new Set();
  const uniqueParts = [];

  for (const p of parts) {
    if (!p || seen.has(p)) continue;
    seen.add(p);
    uniqueParts.push(p);
  }

  return uniqueParts;
}

/**
 * Comma-separated summary for PDP add-to-cart strip.
 * Splits each name on " - " into separate labels; duplicate labels appear once (first occurrence kept).
 *
 * @param {Object|null|undefined} selections
 * @returns {string}
 */
export function buildSelectionSummaryText(selections) {
  return collectSelectionSummaryParts(selections, expandStripSelectionSummaryParts).join(', ');
}

/**
 * Comma-separated summary for configuration flyout footer when all steps are complete.
 * Only the segment before the first " - " per selection.
 *
 * @param {Object|null|undefined} selections
 * @returns {string}
 */
export function buildFlyoutSelectionSummaryText(selections) {
  return collectSelectionSummaryParts(selections, (name) => {
    const label = selectionSummaryDisplayLabel(name);

    return label ? [label] : [];
  }).join(', ');
}
